import React from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { round } from 'lodash';
import { compose, withProps, branch } from 'recompose';

import * as timeplanUnitStyles from './timeplanUnitStyles.css';
import summarizeTimeplanUnits from './summarizeTimeplanUnits';
import * as styles from './timeplan.css';
import timeplanUnitValid from './timeplanUnitValid';
import ToolTip from '../../ToolTip/ToolTip';
import withWorkTypesFromTimeplanUnits from './withWorkTypesFromTimeplanUnits';
import MissingReportedTimes from './MissingReportedTimes';
import { useTranslation } from 'react-i18next';

function Warnings({ timeplanUnits, overlaps, duplicates, }) {
  const { t } = useTranslation();
  if (timeplanUnits.some(timeplanUnit => !!overlaps[timeplanUnit.id])) {
    return (
      <ToolTip
        iconClassName={`fa fa-exclamation-circle text-warning ${styles.timeWarning}`}
        title={t("Överlapp")}
      >
        {t("Något av passen överlappar denna dag!")}
      </ToolTip>
    );
  }

  if (timeplanUnits.some(timeplanUnit => !!duplicates[timeplanUnit.id])) {
    return (
      <ToolTip
        iconClassName={`fa fa-exclamation-circle text-warning ${styles.timeWarning}`}
        title={t("Dublett")}
      >
        {t("Något av passen är en dublett denna dag!")}
      </ToolTip>
    );
  }

  if (timeplanUnits.some(timeplanUnit =>
    !timeplanUnitValid(timeplanUnit))
  ) {
    return (
      <ToolTip
        iconClassName={`fa fa-exclamation-circle text-danger ${styles.timeWarning}`}
        title={t("Felaktigt pass")}
      >
        {t("Något av passen är inte komplett denna dag!")}
      </ToolTip>
    );
  }

  return null;
}

Warnings.propTypes = {
  timeplanUnits: PropTypes.instanceOf(List).isRequired,
};

function SummarizedDay({
  summaries,
  timeplanUnits,
  overlaps,
  duplicates,
  isNotReported,
}) {
  const { t } = useTranslation();
  if (summaries.size === 0) {
    if (isNotReported) {
      return (
        <MissingReportedTimes isNotReported={isNotReported} />
      );
    }

    return (
      <div className="text-muted">
        <i>{t("Inga pass")}</i>
      </div>
    );
  }

  return (
    <div className={timeplanUnitStyles.nonEditableDay}>
      <div className={timeplanUnitStyles.timeplanUnitSummaries}>
        <table>
          <tbody>
            {summaries.map((summary) => {
              const diffMinutes = round(summary.diffMinutes / 60, 2);

              return (
                <tr key={summary.id} className={timeplanUnitStyles.timeplanUnitSummary}>
                  <td>{summary.name}</td>
                  <td>{round(summary.minutes / 60, 2)}h</td>
                  {diffMinutes !== 0 && (
                    <td>
                      <span
                        title={t("Skillnad mot bokad tid")}
                        className={`badge ${styles.marginLeft} ${diffMinutes < 0 ? 'alert-danger' : 'alert-success'}`}
                      >
                        {diffMinutes > 0 ? '+' : ''}
                        {diffMinutes}h
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Warnings
        timeplanUnits={timeplanUnits}
        overlaps={overlaps}
        duplicates={duplicates}
      />
    </div>
  );
}

SummarizedDay.defaultProps = {
  isNotReported: false,
};

SummarizedDay.propTypes = {
  timeplanUnits: PropTypes.instanceOf(List).isRequired,
  summaries: PropTypes.instanceOf(List).isRequired,
  isNotReported: PropTypes.bool,
};

export default compose(
  branch(
    ({ timeplanUnits, compareTimeplanUnits }) => (
      timeplanUnits.size === 0 && compareTimeplanUnits.size === 0
    ),
    withProps({
      summaries: new List(),
    }),
    compose(
      branch(
        ({ workTypes }) => !workTypes,
        withWorkTypesFromTimeplanUnits,
      ),
      withProps(({ timeplanUnits, compareTimeplanUnits, workTypes, absentReasons }) => ({
        summaries: summarizeTimeplanUnits(
          new List(workTypes),
          timeplanUnits.toJS(),
          compareTimeplanUnits.toJS(),
          absentReasons,
        ),
      })),
    ),
  ),
)(SummarizedDay);
